/**
 * Variant Card Picker
 * Custom card-based variant selector for editorial product pages.
 * Syncs with a hidden variant-selects element for theme JS compatibility.
 *
 * Supports two variant schemas:
 * - "bag"  (crisps):    option1=flavour, option2=bag size (large/small + grams), option3=pack count
 * - "pack" (pem bars):  option=flavour + option="N pack" (2 options, either order)
 */
if (!customElements.get('variant-card-picker')) {
  customElements.define('variant-card-picker', class VariantCardPicker extends HTMLElement {
    connectedCallback() {
      const el = this.querySelector('[data-picker-config]');
      if (!el) return;

      const cfg = JSON.parse(el.textContent);
      this.variants = cfg.variants;
      this.optionNames = cfg.options;
      this.currency = cfg.currency || '£';
      this._titleSingle = cfg.cardTitleSingle || 'One large bag';
      this._titleBundle = cfg.cardTitleBundle || '%count% large bags';
      this._titleSmall = cfg.cardTitleSmall || 'Travel-size bags';

      // Per-bar RRP overrides — map of pack count → price string (e.g. "7", "6.50").
      this._perBarRrp = {};
      (cfg.perBarRrp || '').split('\n').forEach((line) => {
        var parts = line.split(':');
        var count = parseInt(parts[0], 10);
        var price = (parts[1] || '').trim();
        if (count > 0 && price) this._perBarRrp[count] = price;
      });

      // Detect which schema we're dealing with
      this._schema = this._detectSchema();

      // Initial state from the server-selected variant
      const sel = this.variants.find(v => v.id === cfg.selectedVariantId);
      this.seasoning = sel ? this._flavourOf(sel) : this._flavourOf(this.variants[0]);

      if (this._schema === 'pack') {
        this.group = sel ? this._group(sel) : this._defaultPackGroup();
        this.bundle = {};
      } else {
        if (sel) {
          this.group = this._group(sel);
          this.bundle = {};
          this.bundle[this.group] = sel.option3;
        } else {
          this.group = 'large_bundle';
          this.bundle = {};
        }

        if (!this.bundle.large_bundle) {
          const bv = this._bundles('large').find(v => v.isBestValue) || this._bundles('large')[0];
          if (bv) this.bundle.large_bundle = bv.option3;
        }
        if (!this.bundle.small_bundle) {
          const first = this._bundles('small')[0];
          if (first) this.bundle.small_bundle = first.option3;
        }
      }

      this._ui = this.querySelector('.vc-picker__ui');
      this._render();
      this._listen();

      // Notify sticky bar of initial selection (price + variant label)
      var self = this;
      setTimeout(function() { self._notifyStickyBar(); }, 100);

      new MutationObserver(function() {
        var labels = self.closest('product-info')?.querySelectorAll('[data-variant-label]');
        if (labels) labels.forEach(function(el) {
          if (!el.textContent) self._notifyStickyBar();
        });
      }).observe(this.closest('product-info') || this, { childList: true, subtree: true });
    }

    /* ---- schema detection ---- */

    _detectSchema() {
      // Pack schema: any variant has option1 or option2 matching "N pack"
      return this.variants.some(function(v) {
        return /\d+\s*pack/i.test(v.option1 || '') || /\d+\s*pack/i.test(v.option2 || '');
      }) ? 'pack' : 'bag';
    }

    _packOf(v) {
      if (this._schema !== 'pack') return null;
      if (/\d+\s*pack/i.test(v.option1 || '')) return v.option1;
      if (/\d+\s*pack/i.test(v.option2 || '')) return v.option2;
      return null;
    }

    _flavourOf(v) {
      if (!v) return '';
      if (this._schema === 'pack') {
        // Whichever option is NOT the pack
        if (/\d+\s*pack/i.test(v.option1 || '')) return v.option2 || '';
        return v.option1 || '';
      }
      return v.option1;
    }

    _flavourOptionName() {
      if (!this.optionNames || !this.optionNames.length) return 'Flavour';
      if (this._schema === 'pack') {
        var v = this.variants[0];
        if (v && /\d+\s*pack/i.test(v.option1 || '')) return this.optionNames[1]?.name || 'Flavour';
        return this.optionNames[0]?.name || 'Flavour';
      }
      return this.optionNames[0]?.name || 'Flavour';
    }

    _defaultPackGroup() {
      var v = this.variants[0];
      if (!v) return 'pack_0';
      return 'pack_' + this._count(this._packOf(v));
    }

    /* ---- variant grouping ---- */

    _group(v) {
      if (this._schema === 'pack') {
        return 'pack_' + this._count(this._packOf(v));
      }
      if (/large/i.test(v.option2)) return /single/i.test(v.option3) ? 'large_single' : 'large_bundle';
      return 'small_bundle';
    }

    _bySeasoning() {
      var self = this;
      return this.variants.filter(function(v) { return self._flavourOf(v) === self.seasoning; });
    }

    _single() { return this._bySeasoning().find(v => this._group(v) === 'large_single'); }

    _bundles(size) {
      const g = size === 'large' ? 'large_bundle' : 'small_bundle';
      return this._bySeasoning()
        .filter(v => this._group(v) === g)
        .sort((a, b) => this._count(a.option3) - this._count(b.option3));
    }

    _packs() {
      var self = this;
      return this._bySeasoning().slice().sort(function(a, b) {
        return self._count(self._packOf(a)) - self._count(self._packOf(b));
      });
    }

    _active() {
      if (this._schema === 'pack') {
        var self = this;
        return this._bySeasoning().find(function(v) { return self._group(v) === self.group; })
          || this._bySeasoning()[0];
      }
      if (this.group === 'large_single') return this._single();
      const sz = this.group === 'large_bundle' ? 'large' : 'small';
      const b = this.bundle[this.group];
      return this._bundles(sz).find(v => v.option3 === b) || this._bundles(sz)[0];
    }

    /* ---- util ---- */

    _count(o) { if (!o) return 1; var m = String(o).match(/(\d+)/); return m ? +m[1] : 1; }
    _grams(o) { if (!o) return 0; var m = String(o).match(/(\d+)\s*g/); return m ? +m[1] : 0; }

    _weight(v) {
      return v.totalWeight || ((this._grams(v.option2) * this._count(v.option3)) + 'g');
    }

    _price(cents) {
      var n = cents / 100;
      return this.currency + (n % 1 === 0 ? String(n) : n.toFixed(2));
    }

    _perBag(v) {
      if (v.perBagPrice) return v.perBagPrice;
      var c = this._count(v.option3);
      return c > 1 ? this._price(Math.round(v.price / c)) + '/bag' : '';
    }

    _seasonings() {
      var self = this;
      var s = new Set();
      return this.variants.map(function(v) { return self._flavourOf(v); })
        .filter(function(x) { return s.has(x) ? false : (s.add(x), true); });
    }

    _esc(s) {
      if (!s) return '';
      var d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML;
    }

    /* ---- render ---- */

    _render() {
      if (!this._ui) return;
      if (this._schema === 'pack') return this._renderPacks();
      return this._renderBags();
    }

    _renderFlavourPills() {
      var h = '';
      var seas = this._seasonings();
      if (seas.length <= 1) return '';

      h += '<div class="vc-seasoning">';
      h += '<span class="vc-seasoning__label">' + this._esc(this._flavourOptionName()) + '</span>';
      h += '<div class="vc-seasoning__pills">';
      for (var i = 0; i < seas.length; i++) {
        var s = seas[i];
        var on = s === this.seasoning;
        h += '<button type="button" class="vc-seasoning__pill' + (on ? ' is-active' : '') + '" '
          + 'data-seasoning="' + this._esc(s) + '" aria-pressed="' + on + '">'
          + this._esc(s) + '</button>';
      }
      h += '</div></div>';
      return h;
    }

    _renderPacks() {
      var self = this;
      var h = '';

      h += this._renderFlavourPills();

      var packs = this._packs();
      var count = packs.length;
      h += '<div class="vc-cards vc-cards--count-' + count + '" role="radiogroup" aria-label="Select your pack">';

      // Best-value: prefer isBestValue flag, else leave none
      var bestIdx = -1;
      for (var i = 0; i < packs.length; i++) {
        if (packs[i].isBestValue) { bestIdx = i; break; }
      }

      for (var j = 0; j < packs.length; j++) {
        var v = packs[j];
        var packName = self._packOf(v);
        var n = self._count(packName);
        var perBar = n > 0 ? self._price(Math.round(v.price / n)) : '';
        var grp = 'pack_' + n;
        var isSel = self.group === grp;
        var isBest = j === bestIdx;

        // Strip any existing "(£X / bar)" suffix; re-append from the editor
        // override when present so per-bar pricing is editable without
        // renaming the Shopify variant.
        var baseTitle = packName.replace(/\s*\(£[^)]*\/\s*bar\)\s*$/i, '');
        var rrp = self._perBarRrp[n];
        var title = rrp ? baseTitle + ' (' + self.currency + rrp + ' / bar)' : packName;

        h += self._card({
          g: grp,
          sel: isSel,
          best: isBest,
          title: title,
          meta: self._price(v.price) + (perBar ? ' · ' + perBar + '/bar' : ''),
          body: v.cardBody,
          ok: v.available,
        });
      }

      h += '</div>';
      this._ui.innerHTML = h;
    }

    _renderBags() {
      var h = '';

      h += this._renderFlavourPills();

      // Cards
      h += '<div class="vc-cards" role="radiogroup" aria-label="Select your pack">';

      // Card 1 — Large single
      var single = this._single();
      if (single) {
        h += this._card({
          g: 'large_single',
          sel: this.group === 'large_single',
          title: this._titleSingle,
          meta: this._weight(single) + ' · ' + this._price(single.price),
          body: single.cardBody,
          ok: single.available,
        });
      }

      // Card 2 — Large bundles (best value)
      var lb = this._bundles('large');
      if (lb.length) {
        var isSel = this.group === 'large_bundle';
        var act = lb.find(v => v.option3 === this.bundle.large_bundle) || lb[0];
        var n = this._count(act.option3);
        h += this._card({
          g: 'large_bundle',
          sel: isSel,
          best: true,
          title: this._titleBundle.replace('%count%', n).replace('{count}', n),
          meta: this._weight(act) + ' · ' + this._price(act.price),
          per: this._perBag(act),
          body: act.cardBody,
          ok: act.available,
          pills: lb.map(v => ({
            label: v.option3,
            val: v.option3,
            on: v.option3 === this.bundle.large_bundle,
            ok: v.available,
          })),
          showPills: true,
        });
      }

      // Card 3 — Small bundles
      var sb = this._bundles('small');
      if (sb.length) {
        var isSel2 = this.group === 'small_bundle';
        var act2 = sb.find(v => v.option3 === this.bundle.small_bundle) || sb[0];
        var entryPerBag = sb[0] ? Math.round(sb[0].price / this._count(sb[0].option3)) : 0;

        h += this._card({
          g: 'small_bundle',
          sel: isSel2,
          title: this._titleSmall,
          meta: isSel2
            ? (this._weight(act2) + ' · ' + this._price(act2.price))
            : (this._grams(sb[0].option2) + 'g each · from ' + this._price(entryPerBag)),
          per: isSel2 ? this._perBag(act2) : '',
          body: isSel2 ? act2.cardBody : (sb[0]?.cardBody || ''),
          ok: isSel2 ? act2.available : sb.some(v => v.available),
          pills: sb.map(v => ({
            label: v.option3,
            val: v.option3,
            on: v.option3 === this.bundle.small_bundle,
            ok: v.available,
          })),
          showPills: isSel2,
        });
      }

      h += '</div>';
      this._ui.innerHTML = h;
    }

    _card(o) {
      var cls = 'vc-card'
        + (o.sel ? ' is-selected' : '')
        + (o.best ? ' vc-card--best' : '')
        + (!o.ok ? ' vc-card--sold-out' : '');

      var h = '<div class="' + cls + '" data-group="' + o.g + '" role="radio" '
        + 'aria-checked="' + !!o.sel + '" tabindex="' + (o.sel ? 0 : -1) + '">';

      if (o.best) h += '<span class="vc-card__badge">Best value</span>';
      h += '<div class="vc-card__title">' + this._esc(o.title) + '</div>';
      h += '<div class="vc-card__meta">' + this._esc(o.meta) + '</div>';
      if (o.per) h += '<div class="vc-card__per">' + this._esc(o.per) + '</div>';
      if (o.body) h += '<div class="vc-card__body">' + this._esc(o.body) + '</div>';

      if (o.pills && o.pills.length > 1) {
        h += '<div class="vc-sub-pills' + (o.showPills ? '' : ' vc-sub-pills--hidden') + '">';
        for (var i = 0; i < o.pills.length; i++) {
          var p = o.pills[i];
          h += '<button type="button" class="vc-sub-pill' + (p.on ? ' is-active' : '') + '" '
            + 'data-bundle="' + this._esc(p.val) + '" data-group="' + o.g + '"'
            + (p.ok ? '' : ' disabled') + '>'
            + this._esc(p.label) + '</button>';
        }
        h += '</div>';
      }

      if (!o.ok) h += '<div class="vc-card__sold-out">Sold out</div>';
      h += '</div>';
      return h;
    }

    /* ---- events (delegated, bound once) ---- */

    _listen() {
      if (this._bound) return;
      this._bound = true;

      this._ui.addEventListener('click', (e) => {
        var sp = e.target.closest('.vc-seasoning__pill');
        var bp = e.target.closest('.vc-sub-pill');
        var cd = e.target.closest('.vc-card');

        if (sp) this._setSeasoning(sp.dataset.seasoning);
        else if (bp) this._setBundle(bp.dataset.group, bp.dataset.bundle);
        else if (cd && !cd.classList.contains('vc-card--sold-out')) this._setGroup(cd.dataset.group);
      });

      this._ui.addEventListener('keydown', (e) => {
        var cd = e.target.closest('.vc-card');
        if (!cd) return;

        var all = Array.from(this._ui.querySelectorAll('.vc-card:not(.vc-card--sold-out)'));
        var idx = all.indexOf(cd);
        var next;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          next = all[(idx + 1) % all.length];
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          next = all[(idx - 1 + all.length) % all.length];
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._setGroup(cd.dataset.group);
          return;
        }

        if (next) {
          next.focus();
          this._setGroup(next.dataset.group);
        }
      });
    }

    _setSeasoning(s) {
      if (s === this.seasoning) return;
      this.seasoning = s;
      this._render();
      this._sync();
    }

    _setGroup(g) {
      if (g === this.group) return;
      this.group = g;
      this._render();
      var card = this._ui.querySelector('[data-group="' + g + '"]');
      if (card) card.focus();
      this._sync();
    }

    _setBundle(group, bundle) {
      if (group !== this.group) this.group = group;
      this.bundle[group] = bundle;
      this._render();
      var pills = this._ui.querySelectorAll('.vc-sub-pill');
      for (var i = 0; i < pills.length; i++) {
        if (pills[i].dataset.bundle === bundle && pills[i].dataset.group === group) {
          pills[i].focus();
          break;
        }
      }
      this._sync();
    }

    /* ---- sync with hidden variant-selects ---- */

    _sync() {
      var v = this._active();
      if (!v) return;

      var vs = this.querySelector('variant-selects');
      if (!vs) return;

      var fsets = vs.querySelectorAll('fieldset');
      var opts = [v.option1, v.option2, v.option3];
      var last = null;

      fsets.forEach(function(fs, i) {
        if (!opts[i]) return;
        var radios = fs.querySelectorAll('input[type="radio"]');
        for (var j = 0; j < radios.length; j++) {
          if (radios[j].value === opts[i] && !radios[j].checked) {
            radios[j].checked = true;
            last = radios[j];
          }
        }
      });

      if (last) last.dispatchEvent(new Event('change', { bubbles: true }));

      this._notifyStickyBar();
    }

    _notifyStickyBar() {
      var v = this._active();
      if (!v) return;

      var parts;
      if (this._schema === 'pack') {
        parts = [this._flavourOf(v), this._packOf(v)].filter(Boolean);
      } else {
        parts = [v.option1, v.option3, v.option2].filter(Boolean);
      }
      var label = parts.join(' · ');

      document.querySelectorAll('[data-variant-label]').forEach(function(el) {
        el.textContent = label;
      });

      document.dispatchEvent(new CustomEvent('variant-card-picker:change', {
        detail: { variantId: v.id, price: v.price, available: v.available, label: label }
      }));
    }
  });
}
